import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useFloatingOffset } from '@/shared/hooks/use-floating-offset'
import { useLocation } from 'react-router-dom'
import { useNav } from '@/shared/hooks/use-nav'
import { Loader2, X } from 'lucide-react'
import { TopBar } from '@/shared/components/top-bar'
import { PlacePicker, type PlaceSelection } from '@/features/lugares/place-picker'
import { Chip } from '@/shared/ui/chip'
import { SegmentedControl } from '@/shared/ui/segmented'
import { Button } from '@/shared/ui/button'
import { IconButton } from '@/shared/ui/icon-button'
import { Toggle } from '@/shared/ui/toggle'
import { LEVEL_BANDS, bandFor, type LevelBand } from '@/shared/levels'
import { showToast } from '@/shared/ui/toast'
import { addCreatedMatch, dayLabelFrom, getProfile, mockClubs } from '@/shared/mock/data'
import { savedContactsList } from '@/shared/player-actions'
import { focusMisTab } from '@/features/mis-partidos/use-mis-partidos'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { placesRepository } from '@/features/lugares/api/places-repository'
import { buildCreateMatchDoc, useCreateMatch, useAddPlaceholder } from '@/features/partidos/use-matches'
import { track } from '@/core/analytics'
import { loadCreateMemory, saveCreateMemory, suggestStart, PRIME_TIMES } from './smart-defaults'
import { useMyProfile } from '@/features/perfil/use-my-profile'
import { MATCH_CAPACITY } from '@/core/models/match'
import { usePlace } from '@/features/lugares/use-places'

// error-not-empty-ok: the contacts list here is BYPASS-ONLY (`AUTH_BYPASS ? savedContactsList() : []`).
// Under real auth this block renders a different sentence entirely — "Al crearlo
// te llevamos al partido para invitar y compartir" — because no invitation is
// sent at this step. There is no query behind the empty branch, so there is no
// failure it could be hiding.

const POSITIONS = ['Derecha', 'Revés', 'Indiferente']
// Competition register, and all four agree with "partido": mixto, masculino,
// femenino. "Solo chicas" was a different grammatical shape bolted into the
// same set — and it calls a 55-year-old a girl, which is friendly between
// friends and presumptuous coming from software.
// 'Cualquiera', NOT 'Abierto'. Privacy already owns Abierto/Privado, so a
// Mixto match rendered a chip saying "Abierto" (publicly listed) and nothing
// about 2+2 — reading as "anyone can join", the opposite of the truth. The
// discovery filter already said "Cualquiera"; now everything does.
const GENDER_OPTIONS = ['Cualquiera', 'Mixto', 'Femenino', 'Masculino'] as const

/** Comma OR dot decimals ("2,50" / "2.5"); null for empty/invalid/≤0. */
function parsePrice(raw: string): number | null {
  const n = Number.parseFloat(raw.trim().replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null
}

// Create match — focused full-screen flow (no tab bar). Persists to the real
// `matches` collection under real auth (organizer = creator, place resolved
// from the places_courts directory), then mirrors into the in-session mock
// store so the still-stubbed Mis partidos stays coherent. Bypass = mock only.
/** "Repetir partido" hands the previous match's setup through router state. */
export interface CrearPrefill {
  placeId?: string
  placeName?: string
  time?: string
  levelKey?: string
  position?: string
  matchType?: 'casual' | 'competitivo'
  privacy?: 'abierto' | 'privado'
  courtReserved?: boolean
  price?: string
  durationMinutes?: number
  genderCat?: (typeof GENDER_OPTIONS)[number]
  whatsappGroupUrl?: string
  /** Repetir carries the previous roster → post-create re-invite (spec/02). */
  reinvite?: { uid: string; name: string }[]
}

function isoToday(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function CrearPartidoPage() {
  // Publishes this screen's bottom chrome height so the update banner and
  // toasts float ABOVE it instead of covering the primary action.
  const setBottomChrome = useFloatingOffset()
  const navigate = useNav()
  const prefill = (useLocation().state as { prefill?: CrearPrefill } | null)?.prefill
  const user = useAuthStore((st) => st.user)
  const createMatch = useCreateMatch()
  const addPlaceholder = useAddPlaceholder()
  const profile = getProfile()
  // Invite targets, MOCK TRACK ONLY. `savedContactsList()` reads a module-level
  // store seeded from `mockContacts` at import, so under real auth this picker
  // rendered Carlos Medina, Ana Pérez, Jorge López and Lucía Ramos to every
  // player — and selecting them wrote names into `players` while sending no
  // `match_invites` doc at all, so the form reported "1 invitación al crear el
  // partido" for invitations that were never sent.
  //
  // Real invites need uids and go through `useSendInvites`, which lives on the
  // match's own invite sheet — where the create flow already lands. So under
  // real auth this picker is empty and the copy points there instead.
  const myContacts = AUTH_BYPASS ? savedContactsList() : []
  // Smart defaults: the form opens already filled with the most probable
  // match (prime-time heuristic + the organizer's last-used values) — only
  // what differs needs touching. Explicit prefill (Repetir) always wins.
  const [memory] = useState(loadCreateMemory)
  // Match level requirement — a band-aligned range; null = Indiferente (any level).
  // Own band prefilled (smart defaults): most organizers seek their own
  // level; Repetir prefills keep THEIR band (even a deliberate null).
  const [levelBand, setLevelBand] = useState<LevelBand | null>(
    prefill
      ? prefill.levelKey
        ? (LEVEL_BANDS.find((b) => b.key === prefill.levelKey) ?? null)
        : null
      : (bandFor(profile.level) ?? null),
  )
  const [place, setPlace] = useState<PlaceSelection>(
    prefill?.placeName ? { kind: 'club', ...(prefill.placeId ? { id: prefill.placeId } : {}), name: prefill.placeName } : null,
  )
  // WHO is coming, not HOW MANY seats are left. The old control asked
  // "¿Cuántos jugadores buscas?" — the DERIVED number — and inferred the known
  // one, which is why it needed a sentence underneath to explain itself. A
  // control that needs a sentence to disambiguate it is asking the wrong
  // question. Missing seats are now arithmetic: 4 − (me + companions).
  //
  // Each name also becomes a REAL placeholder seat with an owner, instead of
  // the anonymous gap the count used to leave. The seat-identity ladder was
  // built precisely so a no-show always answers to somebody; declaring "I'm
  // bringing 2" as a number defeated it on the one path that creates matches.
  const [companions, setCompanions] = useState<Array<{ name: string; gender?: 'male' | 'female' }>>([])
  const [companionDraft, setCompanionDraft] = useState('')

  /** Capacity is fixed at 4 (founder call 2026-08-11 — no singles for now). */
  const freeSeats = Math.max(MATCH_CAPACITY - 1 - companions.length, 0)
  function addCompanion() {
    const name = companionDraft.trim()
    if (!name || companions.length >= 3) return
    setCompanions((prev) => [...prev, { name }])
    setCompanionDraft('')
  }
  const [position, setPosition] = useState(prefill?.position ?? 'Indiferente')
  const [genderCat, setGenderCat] = useState<(typeof GENDER_OPTIONS)[number]>(
    prefill?.genderCat ?? (memory.genderCat as (typeof GENDER_OPTIONS)[number] | undefined) ?? 'Cualquiera',
  )
  const [matchType, setMatchType] = useState<'casual' | 'competitivo'>(prefill?.matchType ?? 'casual')
  const [privacy, setPrivacy] = useState<'abierto' | 'privado'>(prefill?.privacy ?? 'abierto')
  const [courtReserved, setCourtReserved] = useState(prefill?.courtReserved ?? memory.courtReserved ?? true)
  // Home-club preselect (smart defaults, real mode): the organizer's club is
  // the right guess for "¿dónde?" (research F8) — one tap to change. Runs
  // ONCE, never over an explicit prefill or a user pick.
  const homeClubId = useMyProfile().data?.homeClubId ?? null
  const homeClub = usePlace(!AUTH_BYPASS && !prefill && homeClubId ? homeClubId : undefined)
  const prefilledClub = useRef(false)
  useEffect(() => {
    if (prefilledClub.current || prefill || AUTH_BYPASS) return
    if (place === null && homeClub.club) {
      prefilledClub.current = true
      setPlace({ kind: 'club', id: homeClub.club.id, name: homeClub.club.name })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot prefill
  }, [homeClub.club])
  const [suggested] = useState(() => suggestStart(new Date()))
  const [duration, setDuration] = useState<'60' | '90' | '120'>(
    String(prefill?.durationMinutes ?? memory.duration ?? 90) as '60' | '90' | '120',
  )
  const [invited, setInvited] = useState<string[]>([])
  const [notes, setNotes] = useState('')
  const [price, setPrice] = useState(prefill?.price ?? memory.price ?? '')
  const [waGroup, setWaGroup] = useState(prefill?.whatsappGroupUrl ?? memory.waGroup ?? '')
  const [day, setDay] = useState(isoToday(suggested.dayOffset))
  const [time, setTime] = useState(prefill?.time ?? suggested.time)
  const [attempted, setAttempted] = useState(false)
  const [creating, setCreating] = useState(false)

  const toggleInvite = (id: string) =>
    setInvited((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const missingPlace = !place
  // #9 — this used to be a warning the form did not act on: the alert showed,
  // the button stayed live, and a Mixto match was created with a genderless
  // companion the 2+2 rule cannot count. A visible warning that does not block
  // is just a slower way to save bad data.
  const missingCompanionGender =
    genderCat === 'Mixto' && companions.some((c) => !c.gender)
  const missingDay = !day
  const missingTime = !time

  async function onCreate() {
    if (creating) return
    if (missingPlace || missingDay || missingTime || missingCompanionGender) {
      setAttempted(true)
      navigator.vibrate?.(20)
      // A 20ms buzz was the ENTIRE feedback. The errors render inline beside
      // their fields, which on a phone sit ~1000px above the CTA (measured:
      // scrollTop stayed at 1022 after tapping Crear), so the form simply
      // appeared not to respond. Take the player to the first thing that's
      // missing — the errors are rendered by `attempted`, so wait a frame for
      // them to exist before scrolling.
      requestAnimationFrame(() => {
        const target = document.querySelector<HTMLElement>('[data-invalid="true"]')
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
      return
    }
    navigator.vibrate?.(12)
    setCreating(true)

    // Persist first — the confirmation toast only fires on a real write
    // (truthful mutation confirms). Bypass mode has no Firebase session.
    let createdId: string | null = null
    if (!AUTH_BYPASS && user) {
      try {
        const placeDoc =
          place.kind === 'club' && place.id ? await placesRepository.getById(place.id) : null
        createdId = await createMatch.mutateAsync(
          buildCreateMatchDoc({
            organizerUid: user.uid,
            organizerName: profile.name,
            organizerPhotoURL: user.photoURL,
            place: placeDoc,
            manualAddress: place.kind === 'address' ? place.address : null,
            dateISO: day,
            time,
            // 4 − me − companions. Each companion becomes a real seat straight
            // after create (the create rule pins the roster to the organizer),
            // so the match opens with every seat accounted for.
            seatsSought: freeSeats,
            durationMinutes: Number(duration),
            band: levelBand,
            positionLabel: position,
            matchType: matchType === 'competitivo' ? 'Competitivo' : 'Casual',
            privacy: privacy === 'privado' ? 'Privado' : 'Abierto',
            genderLabel: genderCat,
            costPerPlayer: parsePrice(price),
            courtReserved,
            note: notes,
            whatsappGroupUrl: waGroup,
          }),
        )
      } catch {
        setCreating(false)
        navigator.vibrate?.(20)
        showToast('No pudimos crear el partido. Inténtalo de nuevo.')
        return
      }

      // SEAT THE COMPANIONS — a second write per name, never part of the
      // create.
      //
      // `allow create` pins the roster to the organizer alone
      // (`players.hasOnly([request.auth.uid])`, B1 defence-in-depth: nobody
      // pre-seats victims into a match). So names cannot ride the create write,
      // and each one goes through `isAddPlaceholder` — which means each also
      // goes through `vouchHasRoom()`.
      //
      // That is correct rather than awkward: a player at their vouching limit
      // must not be able to seat three more by CREATING a match instead of
      // joining one. But it means a name can legitimately be refused, and the
      // match still exists — so we count what landed and say so, instead of
      // reporting a clean success over a partial one.
      let seated = 0
      for (const c of companions) {
        try {
          await addPlaceholder.mutateAsync({
            matchId: createdId,
            name: c.name,
            createdBy: user.uid,
            ...(c.gender ? { gender: c.gender } : {}),
          })
          seated += 1
        } catch {
          break
        }
      }
      if (seated < companions.length) {
        showToast(
          seated === 0
            ? 'Partido creado, pero no pudimos añadir a tus acompañantes.'
            : `Partido creado con ${seated} de ${companions.length} acompañantes.`,
        )
      }
    }
    // The confirmation must be TRUE: register the match so Mis partidos (and
    // the open feed) show it immediately (one fact, one store).
    const placeName = place.kind === 'club' ? place.name : place.address
    const club = place.kind === 'club' ? mockClubs.find((c) => c.name === place.name) : undefined // mock bridge only
    // The roster must honor the form: me + invited contacts are registered
    // players; the rest of "ya sois N" are off-app companions, so the card
    // math reads "Faltan {slots}" exactly as configured.
    const invitedNames = myContacts.filter((c) => invited.includes(c.id)).map((c) => ({ name: c.name }))
    const seeking = freeSeats
    // "Ya sois N" (me + off-app companions) is fixed by the slots choice;
    // invited contacts FILL sought slots — so the post-create "Faltan" always
    // equals the form's live "quedarán X por cubrir" (cert-walk-7).
    // Mock-store arithmetic only — named `mockCompanionCount` since 2026-08-11
    // because it shadowed the REAL `companions` list and silently won.
    const mockCompanionCount = Math.max(Math.min(4 - seeking - 1, 4 - 1 - invitedNames.length), 0)
    addCreatedMatch({
      id: `c${Date.now() % 100000}`,
      place: placeName,
      city: club?.city ?? 'Granada',
      distanceKm: club?.distanceKm ?? 2.0,
      dayLabel: dayLabelFrom(day),
      dateISO: day,
      timeLabel: time,
      levelMin: levelBand ? levelBand.min : null,
      levelMax: levelBand ? levelBand.max : null,
      position: position as 'Derecha' | 'Revés' | 'Indiferente',
      privacy: privacy === 'privado' ? 'Privado' : 'Abierto',
      matchType: matchType === 'competitivo' ? 'Competitivo' : 'Casual',
      players: [{ name: profile.name }, ...invitedNames],
      offAppCompanions: mockCompanionCount,
      capacity: 4,
      organizer: profile.name,
      organizerMatches: profile.matches,
      organizerNoShows: profile.noShows,
      courtReserved,
      ...(parsePrice(price) != null ? { cost: parsePrice(price) } : {}),
      genderCategory:
        genderCat === 'Femenino'
          ? 'women'
          : genderCat === 'Masculino'
            ? 'men'
            : genderCat === 'Mixto'
              ? 'mixed'
              : 'open',
      status: 'active',
      ...(notes.trim() ? { note: notes.trim() } : {}),
    })
    track('match_created', {
        has_court: courtReserved,
        category: genderCat,
        privacy,
        // Dimensions BigQuery cannot recover later — GA4 does not backfill,
        // so an event that ships without them is unsliceable forever.
        has_price: Boolean(price.trim()),
        duration_min: Number(duration),
      })
    if (createdId) {
      saveCreateMemory({ price, duration, courtReserved, genderCat, waGroup })
      // Was "Partido creado — compártelo para llenarlo". The match page we are
      // about to land on already shows a card reading "Faltan N — compártelo
      // para llenarlo" at y=166 with a WhatsApp button at y=239 — both above
      // the fold, measured. The toast was saying the same sentence a second
      // time, half a second before the card said it better.
      //
      // Auto-opening a share sheet here was considered and rejected for the
      // same reason: it would cover an affordance that is already visible and
      // add a dismiss. (`navigator.share()` was rejected separately — it needs
      // transient user activation, and the Firestore round-trip above expires
      // it, so Safari would refuse silently.)
      showToast('Partido creado')
      void navigate(`/partido/${createdId}`, {
        replace: true,
        // Repetir → auto-open the invite sheet preselected with last time's
        // players (the weekly-group "same match, same people" gesture).
        ...(prefill?.reinvite?.length ? { state: { reinvite: prefill.reinvite } } : {}),
      })
      return
    }
    showToast('Partido creado — lo tienes en Mis partidos')
    focusMisTab('proximos') // the new match must be visible on arrival
    void navigate('/mis-partidos')
  }

  return (
    <>
      <TopBar title="Crear partido" back />
      <div className="flex-1 px-4 py-3 flex flex-col gap-5 pb-28">
        {/* When first, where second — the WhatsApp vernacular this flow
            replaces ("¿pádel a las 7 en Zaidín?"); decision 2026-07-05. */}
        <Section i={0}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Día">
              <input type="date" className="field" value={day} onChange={(e) => setDay(e.target.value)} />
            </Field>
            <Field label="Hora">
              <input type="time" className="field" value={time} onChange={(e) => setTime(e.target.value)} />
            </Field>
          </div>
          <div className="flex gap-2 pt-1">
            <Chip active={day === isoToday()} onClick={() => setDay(isoToday())}>Hoy</Chip>
            <Chip active={day === isoToday(1)} onClick={() => setDay(isoToday(1))}>Mañana</Chip>
          </div>
          {/* Prime-time quick picks — the four slots Spanish padel books. */}
          <div className="flex gap-2 pt-1 overflow-x-auto no-scrollbar">
            {PRIME_TIMES.map((t) => (
              <Chip key={t} active={time === t} onClick={() => setTime(t)}>
                {t}
              </Chip>
            ))}
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <Label>Duración</Label>
            <SegmentedControl<'60' | '90' | '120'>
              value={duration}
              onChange={setDuration}
              options={[
                { value: '60', label: '60 min' },
                { value: '90', label: '90 min' },
                { value: '120', label: '120 min' },
              ]}
            />
          </div>
          {attempted && (missingDay || missingTime) && (
            <FieldError>
              {missingDay && missingTime ? 'Indica el día y la hora.' : missingDay ? 'Indica el día.' : 'Indica la hora.'}
            </FieldError>
          )}
        </Section>

        <Section i={1}>
          <Field label="Lugar">
            <PlacePicker value={place} onChange={setPlace} />
          </Field>
          {attempted && missingPlace && <FieldError>Elige un club o escribe una dirección.</FieldError>}
        </Section>

        <Section i={2}>
          <div className="flex flex-col gap-2">
            <Label>¿Quién viene contigo?</Label>
            {/* Optional. Empty is the common case — you open the match and let
                it fill. Naming somebody seats them, which is why this replaced
                "¿cuántos buscas?": the app now knows WHO, not just how many. */}
            <div className="flex gap-2">
              <input
                value={companionDraft}
                onChange={(e) => setCompanionDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCompanion()
                  }
                }}
                placeholder="Nombre de un acompañante"
                maxLength={40}
                disabled={companions.length >= 3}
                className="flex-1 min-h-[var(--tap)] rounded-[var(--radius)] border border-[var(--line)] bg-[var(--panel-2)] px-3"
              />
              <Button
                variant="outline"
                onClick={addCompanion}
                disabled={!companionDraft.trim() || companions.length >= 3}
              >
                Añadir
              </Button>
            </div>

            {companions.length > 0 && (
              <div className="flex flex-col gap-2">
                {companions.map((c, i) => (
                  <div key={`${c.name}-${i}`} className="flex items-center gap-2">
                    <span className="flex-1 truncate font-medium">{c.name}</span>
                    {/* Mixto is 2+2, so a companion's seat must declare a
                        gender — a seat with none cannot be counted, and the
                        label would be a promise nothing enforces. */}
                    {genderCat === 'Mixto' && (
                      <div className="flex gap-1">
                        {(['female', 'male'] as const).map((g) => (
                          <Chip
                            key={g}
                            active={c.gender === g}
                            onClick={() =>
                              setCompanions((prev) =>
                                prev.map((x, j) => (j === i ? { ...x, gender: g } : x)),
                              )
                            }
                          >
                            {g === 'female' ? 'Mujer' : 'Hombre'}
                          </Chip>
                        ))}
                      </div>
                    )}
                    <IconButton
                      label={`Quitar a ${c.name}`}
                      onClick={() => setCompanions((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <X size={18} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}

            <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
              {freeSeats === 0
                ? 'El partido queda completo — nadie más podrá unirse.'
                : `Faltan ${freeSeats} ${freeSeats === 1 ? 'jugador' : 'jugadores'} por cubrir.`}
            </span>
            {genderCat === 'Mixto' && companions.some((c) => !c.gender) && (
              <FieldError>Indica si cada acompañante es mujer u hombre.</FieldError>
            )}
          </div>
        </Section>

        <Section i={3}>
          <div className="flex flex-col gap-2">
            <Label>Nivel que buscas</Label>
            <div className="flex flex-wrap gap-2">
              <Chip active={levelBand === null} onClick={() => setLevelBand(null)}>Indiferente</Chip>
              {LEVEL_BANDS.map((b) => (
                <Chip key={b.key} active={b.key === levelBand?.key} onClick={() => setLevelBand(b)}>{b.label}</Chip>
              ))}
            </div>
          </div>
        </Section>

        <Section i={4}>
          <div className="flex flex-col gap-2">
            <Label>Posición que buscas</Label>
            <div className="flex flex-wrap gap-2">
              {POSITIONS.map((p) => (
                <Chip key={p} active={p === position} onClick={() => setPosition(p)}>{p}</Chip>
              ))}
            </div>
          </div>
        </Section>

        <Section i={4}>
          <div className="flex flex-col gap-2">
            <Label>¿Quién puede unirse?</Label>
            <div className="flex flex-wrap gap-2">
              {GENDER_OPTIONS.map((g) => (
                <Chip key={g} active={g === genderCat} onClick={() => setGenderCat(g)}>{g}</Chip>
              ))}
            </div>
          </div>
        </Section>

        <Section i={5}>
          <div className="flex flex-col gap-2">
            <Label>Tipo de partido</Label>
            <SegmentedControl<'casual' | 'competitivo'>
              value={matchType}
              onChange={setMatchType}
              options={[
                { value: 'casual', label: 'Casual' },
                { value: 'competitivo', label: 'Competitivo' },
              ]}
            />
          </div>
        </Section>

        <Section i={6}>
          <div className="flex flex-col gap-2">
            <Label>Privacidad</Label>
            <SegmentedControl<'abierto' | 'privado'>
              value={privacy}
              onChange={setPrivacy}
              options={[
                // "Público", not "Abierto". Privacy and the gender audience are different
                // axes and both used to say Abierto, so a Mixto match showed a chip
                // reading "Abierto" and nothing else — it read as "anyone can join".
                // Gender-any became "Cualquiera"; this is the other half.
                { value: 'abierto', label: 'Público' },
                { value: 'privado', label: 'Privado' },
              ]}
            />
            {privacy === 'privado' && (
              <div className="um-fade flex flex-col gap-2 pt-1">
                <Label>Invita a tus contactos</Label>
                {/* Privado = NO sale en el feed. Quien tenga el enlace entra —
                    incluida la mitad de la gente que avisas por WhatsApp y
                    todavía no tiene cuenta. La copia dice exactamente eso
                    porque antes prometía lo contrario. */}
                <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-muted)]">
                  No aparecerá en el feed. Se unirá quien tenga el enlace que compartas.
                </span>
                {!AUTH_BYPASS ? (
                  <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-muted)]">
                    Al crearlo te llevamos al partido para invitar y compartir.
                  </span>
                ) : myContacts.length === 0 ? (
                  <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-muted)]">
                    Aún no tienes contactos guardados — comparte el enlace al crear el partido.
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {myContacts.map((c) => (
                      <Chip key={c.id} active={invited.includes(c.id)} onClick={() => toggleInvite(c.id)}>
                        {c.name.split(' ')[0]}
                      </Chip>
                    ))}
                  </div>
                )}
                {/* Only the mock track can promise invitations from here —
                    under real auth none are sent at this step, so claiming
                    "1 invitación al crear el partido" would be a lie. */}
                {AUTH_BYPASS && (
                  <span
                    className={`text-[length:var(--fs-xs)] ${
                      invited.length === 0 ? 'text-[color:var(--warn)]' : 'text-[color:var(--fg-faint)]'
                    }`}
                  >
                    {/* Was "Sin invitados nadie podrá unirse" — the exact claim
                        retired 2026-08-08, and it sat directly beneath the new
                        sentence saying the opposite. Invitations are a nudge
                        now, not a gate. */}
                    {invited.length === 0
                      ? 'Puedes invitar ahora o compartir el enlace después.'
                      : `${invited.length} ${invited.length === 1 ? 'invitación' : 'invitaciones'} al crear el partido — quedarán ${Math.max(freeSeats - invited.length, 0)} por cubrir.`}
                  </span>
                )}
              </div>
            )}
          </div>
        </Section>

        <Section i={7}>
          <Field label="Precio por jugador (opcional)">
            <input className="field" type="text" inputMode="decimal" placeholder="2,50" value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
          <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
            Como en los grupos: "💰 2,50 €/pala".
          </span>
        </Section>

        <Section i={7}>
          <label className="flex items-center justify-between min-h-[var(--tap)]">
            <span className="font-medium">Ya tengo pista reservada</span>
            <Toggle on={courtReserved} onToggle={() => setCourtReserved((v) => !v)} />
          </label>
        </Section>

        <Section i={8}>
          <Field label="Notas">
            <textarea
              className="field min-h-24 py-3 resize-none"
              placeholder="Buscamos 1 a revés."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
        </Section>

        <Section i={9}>
          <Field label="Grupo de WhatsApp (opcional)">
            <input
              className="field"
              type="url"
              inputMode="url"
              placeholder="https://chat.whatsapp.com/…"
              value={waGroup}
              onChange={(e) => setWaGroup(e.target.value)}
            />
          </Field>
          <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
            Solo lo ven los apuntados — para coordinaros donde ya habláis.
          </span>
        </Section>
      </div>

      <div ref={setBottomChrome} className="chrome-foot safe-bottom sticky bottom-0 px-4 pt-2 pb-3 bg-[var(--chrome-bg)] border-t border-[var(--line)]">
        <Button size="lg" disabled={creating} aria-busy={creating} onClick={() => void onCreate()}>
          {creating ? (
            <>
              <Loader2 size={18} className="motion-safe:animate-spin" /> Creando…
            </>
          ) : (
            'Crear partido'
          )}
        </Button>
      </div>
    </>
  )
}

function Section({ children, i }: { children: ReactNode; i: number }) {
  return (
    <div className="um-enter" style={{ '--i': i } as CSSProperties}>
      {children}
    </div>
  )
}

function Label({ children }: { children: ReactNode }) {
  return <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">{children}</span>
}

/**
 * `role="alert"` so a screen reader announces the problem instead of leaving
 * it as silently-appeared red text, and `data-invalid` so `onCreate` can find
 * the first one to scroll to. pt-2 (8px), not pt-1.5 (6px) — 6px is off the
 * 4/8/12/16/24 ladder.
 */
function FieldError({ children }: { children: ReactNode }) {
  return (
    <span
      role="alert"
      data-invalid="true"
      className="block pt-2 text-[length:var(--fs-xs)] text-[color:var(--bad)]"
    >
      {children}
    </span>
  )
}

function Field({ label, icon, children }: { label: string; icon?: ReactNode; children: ReactNode }) {
  // <label> wrapping = automatic accessible name for the contained control
  // (axe: native date/time inputs have no placeholder fallback).
  return (
    <label className="flex flex-col gap-2">
      <Label>{label}</Label>
      <div className="relative">
        {icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--fg-faint)] pointer-events-none">
            {icon}
          </span>
        )}
        {children}
      </div>
    </label>
  )
}
